import { useState, useEffect, useRef, type CSSProperties } from "react";
import { showToast } from "../primitives";
import { useCreateRoutine } from "../../hooks/use-routines";
import type { Routine } from "../../../lib/types";
import { modalOverlay, modalBox, fieldStyle, labelStyle, inputStyle } from "./task-modal-shared";

interface NewRoutineModalProps {
  open: boolean;
  onClose: () => void;
  /** Agent node the routine is declared on. */
  agentId: string;
  onCreated?: (routine: Routine) => void;
}

/** Common cadence patterns offered as suggestions; the field stays free-text. */
const CADENCE_SUGGESTIONS = [
  "0 * * * *",
  "0 9 * * 1-5",
  "0 9 * * 1",
  "ad-hoc",
  "on inbound email matching \"RFP\"",
];

const OUTPUT_SUGGESTIONS = ["task", "activity", "page", "memory", "opportunity"];

/**
 * Sprint 14 — declare a Routine on an agent. Mirrors NewTaskModal. The prompt is
 * stored inline (prompt_body); promotion to a shared Prompt is a later gesture.
 */
export function NewRoutineModal({ open, onClose, agentId, onCreated }: NewRoutineModalProps) {
  const [name, setName] = useState("");
  const [purpose, setPurpose] = useState("");
  const [cadence, setCadence] = useState("");
  const [promptBody, setPromptBody] = useState("");
  const [completion, setCompletion] = useState("");
  const [outputs, setOutputs] = useState<string[]>([]);
  const nameRef = useRef<HTMLInputElement>(null);
  const createRoutine = useCreateRoutine();

  useEffect(() => {
    if (open) {
      setName("");
      setPurpose("");
      setCadence("");
      setPromptBody("");
      setCompletion("");
      setOutputs([]);
      setTimeout(() => nameRef.current?.focus(), 50);
    }
  }, [open]);

  if (!open) return null;

  const valid = name.trim() && purpose.trim() && cadence.trim() && promptBody.trim();

  function toggleOutput(slug: string) {
    setOutputs((prev) => (prev.includes(slug) ? prev.filter((s) => s !== slug) : [...prev, slug]));
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!valid) return;
    createRoutine.mutate(
      {
        agent_id: agentId,
        name: name.trim(),
        purpose: purpose.trim(),
        cadence: cadence.trim(),
        prompt_body: promptBody.trim(),
        completion_criteria: completion.trim() || undefined,
        expected_outputs: outputs.length > 0 ? outputs : undefined,
      },
      {
        onSuccess: (routine) => {
          showToast(`Routine '${routine.name}' created`);
          onCreated?.(routine);
          onClose();
        },
        onError: (err) => {
          const msg = err instanceof Error ? err.message : "Something went wrong";
          showToast(`Couldn't create routine: ${msg}`, "error", true);
        },
      },
    );
  }

  return (
    <div style={modalOverlay} onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div style={modalBox} role="dialog" aria-label="New routine">
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20 }}>
          <div style={iconBox}>
            <i className="ti ti-repeat" aria-hidden="true" />
          </div>
          <div>
            <div style={{ fontSize: 15, fontWeight: 500, color: "var(--color-text-primary)" }}>New routine</div>
            <div style={{ fontSize: 12, color: "var(--color-text-tertiary)" }}>A recurring duty — hosts run it, Kuvio just declares it</div>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          <div style={fieldStyle}>
            <label style={labelStyle}>Name</label>
            <input
              ref={nameRef}
              type="text"
              style={inputStyle}
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Hourly inbox triage"
              maxLength={120}
            />
          </div>

          <div style={fieldStyle}>
            <label style={labelStyle}>Purpose</label>
            <input
              type="text"
              style={inputStyle}
              value={purpose}
              onChange={(e) => setPurpose(e.target.value)}
              placeholder="Why this exists — e.g. Stay on top of inbound before SLA"
              maxLength={200}
            />
          </div>

          <div style={fieldStyle}>
            <label style={labelStyle}>Cadence</label>
            <input
              type="text"
              style={inputStyle}
              value={cadence}
              onChange={(e) => setCadence(e.target.value)}
              placeholder='A cron expression, a trigger phrase, or "ad-hoc"'
              list="routine-cadence-suggestions"
            />
            <datalist id="routine-cadence-suggestions">
              {CADENCE_SUGGESTIONS.map((c) => <option key={c} value={c} />)}
            </datalist>
          </div>

          <div style={fieldStyle}>
            <label style={labelStyle}>Prompt</label>
            <textarea
              style={{ ...inputStyle, minHeight: 100, resize: "vertical", fontFamily: "var(--font-mono, monospace)" }}
              value={promptBody}
              onChange={(e) => setPromptBody(e.target.value)}
              placeholder="The work to do, in markdown. Hosts read this and execute it."
            />
          </div>

          <div style={fieldStyle}>
            <label style={labelStyle}>Completion criteria <span style={{ color: "var(--color-text-tertiary)", fontWeight: 400 }}>(optional)</span></label>
            <input
              type="text"
              style={inputStyle}
              value={completion}
              onChange={(e) => setCompletion(e.target.value)}
              placeholder='e.g. All P1 items triaged into Tasks'
            />
          </div>

          <div style={fieldStyle}>
            <label style={labelStyle}>Expected outputs <span style={{ color: "var(--color-text-tertiary)", fontWeight: 400 }}>(optional)</span></label>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {OUTPUT_SUGGESTIONS.map((slug) => {
                const on = outputs.includes(slug);
                return (
                  <button
                    key={slug}
                    type="button"
                    onClick={() => toggleOutput(slug)}
                    style={chip(on)}
                  >
                    {on && <i className="ti ti-check" style={{ fontSize: 11 }} aria-hidden="true" />} {slug}
                  </button>
                );
              })}
            </div>
          </div>

          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 20 }}>
            <button type="button" onClick={onClose} style={{ fontSize: 13, padding: "8px 14px" }}>Cancel</button>
            <button
              type="submit"
              data-variant="primary"
              disabled={!valid || createRoutine.isPending}
              style={{ fontSize: 13, padding: "8px 14px" }}
            >
              {createRoutine.isPending ? "Creating..." : "Create routine"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

const iconBox: CSSProperties = {
  width: 40,
  height: 40,
  borderRadius: "var(--border-radius-md)",
  background: "var(--color-background-secondary)",
  border: "var(--kuvio-border-default)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color: "var(--color-text-secondary)",
  fontSize: 20,
  flexShrink: 0,
};

const chip = (on: boolean): CSSProperties => ({
  fontSize: 12,
  padding: "4px 10px",
  borderRadius: "var(--border-radius-pill, 999px)",
  border: on ? "1px solid var(--c-teal-600)" : "var(--kuvio-border-default)",
  background: on ? "var(--c-teal-600)" : "var(--color-background-primary)",
  color: on ? "#fff" : "var(--color-text-secondary)",
  cursor: "pointer",
  fontFamily: "var(--font-sans)",
});
